import {
  NavigationModalDialog_default
} from "./chunk-TFUXYORB.js";
import {
  PreventUnmounting_default
} from "./chunk-PT5DIVZ3.js";
import {
  muiMergeSx
} from "./chunk-F3QHBCNP.js";

// src/NavigationModal.tsx
import { useModal, useModalTopKey } from "@levana/utils/modal";
import Modal from "@mui/joy/Modal";
import { styled, useThemeProps } from "@mui/joy/styles";
import useSlot from "@mui/joy/utils/useSlot";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { jsx } from "react/jsx-runtime";
var name = "LevanaNavigationModal";
var NavigationModalRoot = styled(Modal, {
  name,
  slot: "root"
})({});
var _NavigationModalDialog = styled(NavigationModalDialog_default, {
  name,
  slot: "dialog"
})({});
var NavigationModal = forwardRef(
  function NavigationModal2(inProps, ref) {
    const props = useThemeProps({ props: inProps, name });
    const { rootId, onDismiss, children, ...otherProps } = props;
    const ownerState = { ...props };
    const externalForwardedProps = { ...otherProps };
    const [SlotRoot, rootProps] = useSlot("root", {
      ref,
      className: "root",
      elementType: NavigationModalRoot,
      ownerState,
      externalForwardedProps
    });
    const [SlotDialog, dialogProps] = useSlot("dialog", {
      ref,
      className: "dialog",
      elementType: _NavigationModalDialog,
      ownerState,
      externalForwardedProps
    });
    const { dismiss } = useModal();
    const topModalKey = useModalTopKey();
    const initialTopModalKey = useRef(topModalKey);
    const [dynamicItemMap, setDynamicItemMap] = useState({});
    const [stack, setStack] = useState([
      {
        type: "dynamic",
        id: rootId
      }
    ]);
    const topItem = (() => {
      const topStackItem = stack.at(stack.length - 1);
      if (!topStackItem) {
        return void 0;
      }
      switch (topStackItem.type) {
        case "dynamic":
          return dynamicItemMap[topStackItem.id];
        case "static":
          return topStackItem;
      }
    })();
    const handleDismiss = useCallback(() => {
      dismiss();
      props.onDismiss?.();
    }, [dismiss, props.onDismiss]);
    const handleDismissRef = useRef(handleDismiss);
    useEffect(() => {
      handleDismissRef.current = handleDismiss;
    }, [handleDismiss]);
    useEffect(() => {
      if (topItem?.canClose) {
        const handleKeyDown = (event) => {
          if (event.key === "Escape") {
            handleDismissRef.current();
          }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
        };
      }
    }, [topItem?.canClose]);
    return /* @__PURE__ */ jsx(
      NavigationModalContext.Provider,
      {
        value: {
          id: topItem?.id ?? rootId,
          setDynamicItemMap
        },
        children: /* @__PURE__ */ jsx(
          SlotRoot,
          {
            ...rootProps,
            open: true,
            onClose: topItem?.canClose ? () => handleDismissRef.current() : void 0,
            sx: [
              (theme) => muiMergeSx(theme, props.sx),
              topModalKey !== initialTopModalKey.current && {
                display: "none"
              }
            ],
            children: /* @__PURE__ */ jsx(
              SlotDialog,
              {
                ...dialogProps,
                rootId,
                stack,
                setStack,
                topItem,
                onDismiss,
                children
              }
            )
          }
        )
      }
    );
  }
);
var NavigationModalDynamicItem = (props) => {
  const navigationModalContext = useContext(NavigationModalContext);
  const { canClose, canPop, subtitle, title } = props;
  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  }, [props]);
  useEffect(() => {
    navigationModalContext?.setDynamicItemMap((map) => ({
      ...map,
      [propsRef.current.id]: {
        id: propsRef.current.id,
        canClose,
        canPop,
        icon: propsRef.current.icon,
        subtitle,
        title,
        helper: propsRef.current.helper
      }
    }));
  }, [
    navigationModalContext?.setDynamicItemMap,
    canClose,
    canPop,
    subtitle,
    title
  ]);
  return /* @__PURE__ */ jsx(PreventUnmounting_default, { show: navigationModalContext?.id === props.id, children: props.children });
};
var NavigationModalContext = createContext(void 0);
var useNavigationModalTopId = () => {
  return useContext(NavigationModalContext)?.id ?? "";
};
var NavigationModal_default = NavigationModal;

export {
  NavigationModalDynamicItem,
  useNavigationModalTopId,
  NavigationModal_default
};
